import React, { useCallback, useEffect, useState } from 'react';
import { downloadTeamFile, getTeamAudit, type AuditItem } from '../../../api/reportingApi';

/** Customer admin history (SRS #125), export audit (SRS #128) and the team data export (SRS #127). */
const TeamActivityPanel: React.FC = () => {
  const [items, setItems] = useState<AuditItem[]>([]);
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [exportsOnly, setExportsOnly] = useState(false);
  const [actor, setActor] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    getTeamAudit({ exports_only: exportsOnly, actor: actor || undefined, limit: 100 })
      .then((r) => { setItems(r.items); setLabels(r.labels); })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'));
  }, [exportsOnly, actor]);
  useEffect(() => { load(); }, [load]);

  const dl = async (what: 'audit.csv' | 'export') => {
    if (what === 'export' && !window.confirm('Export the whole workspace (members, roles, folders, notes, comments, sign-ins, usage, activity) as a ZIP? The export is recorded in the activity history.')) return;
    setBusy(what); setError(null);
    try { await downloadTeamFile(what); load(); } catch (e) { setError(e instanceof Error ? e.message : 'Download failed'); } finally { setBusy(null); }
  };

  const border = 'var(--ds-border, var(--ds-border))';
  const muted = 'var(--color-text-muted, var(--ds-text-muted))';
  const text = 'var(--color-text, var(--ds-text))';
  const btn: React.CSSProperties = { padding: '5px 10px', borderRadius: 6, border: `1px solid ${border}`, background: 'var(--ds-surface, #fff)', cursor: 'pointer', fontSize: 12.5, color: text };

  return (
    <section style={{ border: `1px solid ${border}`, borderRadius: 14, padding: 18, background: 'var(--ds-surface, #fff)', marginTop: 18 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 12 }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: text }}>Activity history</div>
          <div style={{ fontSize: 12.5, color: muted }}>Every team change and every download, with who and when.</div>
        </div>
        <input placeholder="Filter by person" value={actor} onChange={(e) => setActor(e.target.value)} style={{ ...btn, cursor: 'text', minWidth: 160 }} />
        <label style={{ fontSize: 12.5, color: muted }}><input type="checkbox" checked={exportsOnly} onChange={(e) => setExportsOnly(e.target.checked)} /> exports only</label>
        <button type="button" style={btn} disabled={!!busy} onClick={() => dl('audit.csv')}>{busy === 'audit.csv' ? '…' : 'CSV'}</button>
        <button type="button" style={{ ...btn, background: 'var(--ds-brand-blue)', color: 'var(--ds-text-on-dark)', border: 'none', fontWeight: 600 }} disabled={!!busy} onClick={() => dl('export')}>{busy === 'export' ? 'Building…' : 'Export team data (ZIP)'}</button>
      </div>
      {error && <div style={{ color: 'var(--ds-danger-text)', fontSize: 13, marginBottom: 8 }}>{error}</div>}
      {items.length === 0 ? <div style={{ color: muted, fontSize: 13 }}>Nothing recorded yet.</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead><tr style={{ color: muted, textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {['When', 'What', 'Who', 'Target', 'IP'].map((h) => <th key={h} style={{ padding: '6px 8px', borderBottom: `1px solid ${border}` }}>{h}</th>)}
            </tr></thead>
            <tbody>{items.map((it) => (
              <tr key={it.id}>
                <td style={{ padding: '6px 8px', borderBottom: `1px solid ${border}`, color: muted, whiteSpace: 'nowrap' }}>{it.created_at ? new Date(it.created_at).toLocaleString() : ''}</td>
                <td style={{ padding: '6px 8px', borderBottom: `1px solid ${border}`, color: text }}>
                  <span style={{ padding: '1px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: it.action.startsWith('export.') ? 'var(--ds-warning-bg)' : 'var(--ds-surface-2)', color: it.action.startsWith('export.') ? 'var(--ds-warning)' : 'var(--ds-info)' }}>{labels[it.action] || it.label}</span>
                  {it.detail?.format ? <span style={{ marginLeft: 6, color: muted }}>{String(it.detail.format)}</span> : null}
                </td>
                <td style={{ padding: '6px 8px', borderBottom: `1px solid ${border}`, color: text }}>{it.actor || '—'}</td>
                <td style={{ padding: '6px 8px', borderBottom: `1px solid ${border}`, color: muted }}>{it.target_label || it.target_id || '—'}</td>
                <td style={{ padding: '6px 8px', borderBottom: `1px solid ${border}`, color: muted, fontFamily: 'ui-monospace, monospace' }}>{it.ip_address || '—'}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default TeamActivityPanel;
